import { useTranslation } from 'react-i18next';
import { Card } from '../atoms/Card.js';
import { Typography } from '../atoms/Typography.js';

interface ReportViewerProps {
  content: string;
  generatedAt?: string;
  className?: string;
}

function renderMarkdownLine(line: string, index: number) {
  if (line.startsWith('### ')) {
    return (
      <Typography key={index} variant="h5" className="mt-4 mb-2">
        {line.slice(4)}
      </Typography>
    );
  }
  if (line.startsWith('## ')) {
    return (
      <Typography key={index} variant="h4" className="mt-6 mb-2">
        {line.slice(3)}
      </Typography>
    );
  }
  if (line.startsWith('# ')) {
    return (
      <Typography key={index} variant="h3" className="mt-6 mb-2">
        {line.slice(2)}
      </Typography>
    );
  }
  if (line.startsWith('- ') || line.startsWith('* ')) {
    return (
      <li key={index} className="ml-4 text-sm text-neutral-700">
        {renderInlineFormatting(line.slice(2))}
      </li>
    );
  }
  if (/^\d+\.\s/.test(line)) {
    const text = line.replace(/^\d+\.\s/, '');
    return (
      <li key={index} className="ml-4 list-decimal text-sm text-neutral-700">
        {renderInlineFormatting(text)}
      </li>
    );
  }
  if (line.trim() === '') {
    return <div key={index} className="h-2" />;
  }
  return (
    <Typography key={index} variant="body-sm" className="mb-1">
      {renderInlineFormatting(line)}
    </Typography>
  );
}

function renderInlineFormatting(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
}

export function ReportViewer({ content, generatedAt, className = '' }: ReportViewerProps) {
  const { t, i18n } = useTranslation('dashboard');
  const lines = content.split('\n');

  return (
    <Card padding="lg" className={className}>
      <div className="mb-4 flex items-center justify-between">
        <Typography variant="h3">{t('report.title')}</Typography>
        {generatedAt && (
          <Typography variant="caption">
            {t('report.generated_at')}{' '}
            {new Date(generatedAt).toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : 'en-US')}
          </Typography>
        )}
      </div>
      <div className="prose-sm">{lines.map((line, index) => renderMarkdownLine(line, index))}</div>
    </Card>
  );
}
